// src/pages/AIZone.ts
import "./AIZone.css";
import "../components/AIZoneBatchResponse.css";
import { renderAIRichText } from "../components/AIRichText";
import { renderAIZoneSidebar } from "../components/AIZoneSidebar";
import { renderAIZoneSearchBar } from "../components/AIZoneSearchBar";
import {
  renderCodeBlocks,
  type AICodeBlock,
} from "../components/Code";
import { getClientTimeContext } from "../utils/clientTimeContext";
import { compactLocationHint, getFreshWeatherLocationContext, isNearMeQuery, isCurrentLocationQuery } from "../components/UserLocation";
import { renderAIZoneFeedback } from "../components/AIZoneFeedback";
import type { CurrencyResult } from "../components/currency/CurrencyTypes";
import type { TranslationOverviewPayload } from "../components/AiOverviewTranslator";

interface AISource {
  title?: string;
  url?: string;
  domain?: string;
  snippet?: string;
}

interface AIZoneBatchItem {
  question?: string;
  answer?: string;
  response_type?: string;
  timezone?: string;
  location?: string;
  date?: string;
  time_12h?: string;
}

interface Message {
  role: "user" | "ai";
  text: string;
  codeBlocks?: AICodeBlock[];
  sources?: AISource[];
  timestamp?: Date;
  showAccuracyDisclaimer?: boolean;
  batchItems?: AIZoneBatchItem[];
  responseType?: string;
  currency?: CurrencyResult;
  showCurrencyComponent?: boolean;
  forceTodaysCurrencyComponent?: boolean;
  translation?: TranslationOverviewPayload;
}

interface AIZoneApiResponse {
  degraded?: boolean;
  cooldown?: boolean;
  answer?: string;
  error?: string;
  conversation_id?: string;
  rewritten_query?: string;
  grounded?: boolean;
  memory_answer?: boolean;
  history_turns?: number;
  sources?: AISource[];
  code_blocks?: AICodeBlock[];
  timezone?: string;
  timezone_source?: string;
  deterministic?: boolean;
  response_type?: "date" | "time" | "weather" | string;
  batch?: boolean;
  batch_count?: number;
  items?: AIZoneBatchItem[];
  weather?: boolean;
  currency?: CurrencyResult;
  translation?: TranslationOverviewPayload;
}

const AI_ZONE_API_URL = "https://api.ayesearch.com/api/ai/zone/";
const AI_ZONE_CONVERSATION_KEY = "ayesearch_ai_zone_conversation_id";

const AI_ZONE_APPS_LIST = [
  {
    name: "VibeJoos",
    icon: "▶",
    color: "#ff6b6b",
    image: "",
  },
  {
    name: "AMailMe",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/Amailme_Icon.png",
  },
  {
    name: "OreChat",
    icon: "",
    color: "transparent",
    image: "/static/ayesearch/OreChat_Icon.png",
  },
  {
    name: "ADrive",
    icon: "☁",
    color: "#ffd43b",
    image: "",
  },
  {
    name: "ADocs",
    icon: "📄",
    color: "#845ef7",
    image: "",
  },
  {
    name: "ANote",
    icon: "📝",
    color: "#f06595",
    image: "",
  },
];

let messages: Message[] = [];
let autoSubmittedInitialQuery = "";

function getInitialQueryFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return (params.get("q") || "").trim();
}

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function generateAIZoneAppsMenuHtml(): string {
  return `
    <div class="aiz-apps-menu hidden" id="apps-menu">
      <div class="aiz-apps-menu-grid">
        ${AI_ZONE_APPS_LIST.map(
          (app) => `
            <div class="aiz-app-item">
              ${
                app.image
                  ? `<img
                      class="aiz-app-icon aiz-app-icon-image"
                      src="${app.image}"
                      alt="${escapeHtml(app.name)}"
                    />`
                  : `<div
                      class="aiz-app-icon"
                      style="background-color: ${app.color}"
                      aria-hidden="true"
                    >${app.icon}</div>`
              }
              <span>${escapeHtml(app.name)}</span>
            </div>
          `,
        ).join("")}
      </div>

      <div class="aiz-apps-download-hub">
        <div class="aiz-apps-download-hub-header">
          <img
            class="aiz-apps-browser-icon"
            src="/static/ayesearch/Ayesearch_Icon.png"
            alt="AyeSearch"
          />
          <div class="aiz-apps-browser-copy">
            <strong>Get AyeSearch</strong>
            <span>Choose mobile or desktop browser.</span>
          </div>
        </div>

        <div class="aiz-apps-download-platforms">
          <div class="aiz-apps-download-platform">
            <span class="aiz-apps-download-kicker">Mobile app</span>
            <strong>iOS / Android</strong>
            <a
              class="aiz-apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch for iOS or Android"
            >
              Download
            </a>
          </div>

          <div class="aiz-apps-download-platform">
            <span class="aiz-apps-download-kicker">AyeSearch Browser</span>
            <strong>Mac / Windows</strong>
            <a
              class="aiz-apps-browser-download-btn"
              href="https://ayesearch.com/downloads/update"
              aria-label="Download AyeSearch Browser for Mac or Windows"
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function renderAIZone() {
  const app = document.getElementById("app");
  if (!app) return;

  app.innerHTML = `
    <div class="ai-wrapper">
      <div id="ai-sidebar-container"></div>

      <div class="ai-main">
        <div class="ai-topnav">
          <div class="nav-left">
            <button class="hamburger" id="hamburger-btn">☰</button>
            <button class="nav-item active">AI Zone</button>
            <button class="nav-item">All</button>
            <button class="nav-item">Images</button>
          </div>

          <div class="nav-right">
            <div class="aiz-apps-dropdown-container">
              <button
                class="aiz-apps-btn"
                id="apps-btn"
                type="button"
                aria-label="Open apps"
                aria-expanded="false"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <circle cx="5" cy="5" r="2.5"/>
                  <circle cx="12" cy="5" r="2.5"/>
                  <circle cx="19" cy="5" r="2.5"/>
                  <circle cx="5" cy="12" r="2.5"/>
                  <circle cx="12" cy="12" r="2.5"/>
                  <circle cx="19" cy="12" r="2.5"/>
                  <circle cx="5" cy="19" r="2.5"/>
                  <circle cx="12" cy="19" r="2.5"/>
                  <circle cx="19" cy="19" r="2.5"/>
                </svg>
              </button>

              ${generateAIZoneAppsMenuHtml()}
            </div>

            <button class="signin-btn" id="signin-btn">Sign In</button>
          </div>
        </div>

        <div class="ai-chat-feed" id="chat-feed"></div>

        <div class="ai-chat-input">
          <div id="ai-searchbar-container"></div>
        </div>
      </div>
    </div>
  `;

  setupAIZoneEvents();
}

function setupAIZoneEvents() {
  const sidebarContainer = document.getElementById("ai-sidebar-container");
  if (sidebarContainer) renderAIZoneSidebar(sidebarContainer);

  const searchContainer = document.getElementById("ai-searchbar-container");
  if (searchContainer) {
    renderAIZoneSearchBar(searchContainer, handleSend);
  }

  const hamburgerBtn = document.getElementById("hamburger-btn");

  hamburgerBtn?.addEventListener("click", (event) => {
    event.stopPropagation();
    document.getElementById("ai-sidebar")?.classList.toggle("open");
  });

  const appsBtn = document.getElementById("apps-btn");
  const appsMenu = document.getElementById("apps-menu");

  appsBtn?.addEventListener("click", (event) => {
    event.stopPropagation();

    if (!appsMenu) return;

    const isHidden = appsMenu.classList.contains("hidden");
    appsMenu.classList.toggle("hidden", !isHidden);
    appsBtn.setAttribute("aria-expanded", String(isHidden));
  });

  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    // Close AI Zone sidebar when clicking outside it.
    const sidebar = document.getElementById("ai-sidebar");
    if (
      sidebar?.classList.contains("open") &&
      !sidebar.contains(target) &&
      !hamburgerBtn?.contains(target)
    ) {
      sidebar.classList.remove("open");

      const sidebarToggle = document.getElementById("sidebar-toggle");
      if (sidebarToggle) sidebarToggle.textContent = "☰";
    }

    // Close apps dropdown when clicking outside it.
    if (
      appsMenu &&
      !appsMenu.contains(target) &&
      !appsBtn?.contains(target)
    ) {
      appsMenu.classList.add("hidden");
      appsBtn?.setAttribute("aria-expanded", "false");
    }
  });

  document.getElementById("signin-btn")?.addEventListener("click", () => {
    const next = encodeURIComponent(window.location.href);
    window.location.assign(
      `https://api.ayesearch.com/auth/ayesearch/login/?next=${next}`,
    );
  });

  renderMessages();

  const initialQuery = getInitialQueryFromUrl();
  if (initialQuery && initialQuery !== autoSubmittedInitialQuery) {
    autoSubmittedInitialQuery = initialQuery;

    // Run after the AI Zone layout/search bar has finished mounting.
    window.setTimeout(() => {
      void handleSend(initialQuery);
    }, 0);
  }
}



function isHighConfidenceUtilityQuery(query: string): boolean {
  const value = String(query || "").trim().toLowerCase();
  if (!value) return false;

  const weather =
    /\bweather\b/.test(value) ||
    /\bforecast\b/.test(value) ||
    /\btemperature\b/.test(value);

  const date =
    /\b(today|today's|todays|tomorrow|tommorow|yesterday|date)\b/.test(value) ||
    /\b(next|last)\s+week\b/.test(value) ||
    /\bwhat day is it\b/.test(value);

  const time =
    /\bwhat time\b/.test(value) ||
    /\bcurrent time\b/.test(value) ||
    /\blocal time\b/.test(value) ||
    /\btime near me\b/.test(value) ||
    /\btime in\b/.test(value);

  return weather || date || time;
}

function shouldShowAccuracyDisclaimer(
  data: AIZoneApiResponse,
  query: string,
): boolean {
  const responseType = String(data.response_type || "").toLowerCase();

  // Backend metadata is authoritative. Deterministic AyeSearch utility answers
  // are computed from ZoneInfo/calendar math or the weather cache, not an LLM.
  if (
    data.deterministic === true &&
    (responseType === "date" ||
      responseType === "time" ||
      responseType === "weather" ||
      responseType === "utility" ||
      responseType === "market_data" ||
      responseType === "currency" ||
      data.weather === true)
  ) {
    return false;
  }

  // Compatibility fallback while multiple backend nodes roll out the update.
  if (isHighConfidenceUtilityQuery(query)) {
    return false;
  }

  return true;
}


function cleanBatchQuestion(value: string): string {
  return String(value || "")
    .trim()
    .replace(/[?]+$/g, "")
    .replace(/\s+/g, " ");
}

function conciseBatchAnswer(value: string): string {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function parsePlainBatchItems(text: string): AIZoneBatchItem[] {
  const value = String(text || "").trim();
  if (!value || !value.includes("•")) return [];

  const items: AIZoneBatchItem[] = [];

  // Primary format produced by AyeSearch deterministic batch resolver:
  // • Question — Answer
  const bulletPattern =
    /(?:^|\n)\s*•\s*(.+?)\s+(?:—|–|-)\s+(.+?)(?=(?:\n\s*•)|$)/gs;

  for (const match of value.matchAll(bulletPattern)) {
    const question = cleanBatchQuestion(match[1] || "");
    const answer = conciseBatchAnswer(match[2] || "");

    if (question && answer) {
      items.push({ question, answer });
    }
  }

  // Be conservative: only switch to structured rendering when at least two
  // valid batch rows were identified.
  return items.length >= 2 ? items : [];
}

function effectiveBatchItems(msg: Message): AIZoneBatchItem[] {
  if (msg.batchItems?.length) return msg.batchItems;
  return parsePlainBatchItems(msg.text);
}

function renderBatchItemsHtml(items: AIZoneBatchItem[]): string {
  const safeItems = items.filter(
    (item) =>
      String(item.question || "").trim() &&
      String(item.answer || "").trim(),
  );

  if (!safeItems.length) return "";

  return `
    <div class="aiz-batch-response">
      <div class="aiz-batch-intro">
        Here are the exact answers, calculated in each location's local time:
      </div>
      <ul class="aiz-batch-list">
        ${safeItems.map(
          (item) => `
            <li class="aiz-batch-item">
              <strong class="aiz-batch-question">${escapeHtml(
                cleanBatchQuestion(item.question || ""),
              )}:</strong>
              <span class="aiz-batch-answer">${escapeHtml(
                conciseBatchAnswer(item.answer || ""),
              )}</span>
            </li>
          `,
        ).join("")}
      </ul>
    </div>
  `;
}

function fullMessageText(msg: Message): string {
  const parts: string[] = [];

  const batchItems = effectiveBatchItems(msg);

  if (batchItems.length) {
    const batchLines = batchItems
      .filter(
        (item) =>
          String(item.question || "").trim() &&
          String(item.answer || "").trim(),
      )
      .map(
        (item) =>
          `• ${cleanBatchQuestion(item.question || "")}: ${conciseBatchAnswer(
            item.answer || "",
          )}`,
      );

    if (batchLines.length) {
      parts.push(
        "Here are the exact answers, calculated in each location's local time:\n" +
          batchLines.join("\n"),
      );
    }
  } else if (msg.text.trim()) {
    parts.push(msg.text.trim());
  }

  if (msg.codeBlocks?.length) {
    for (const block of msg.codeBlocks) {
      const language = String((block as any).language || "").trim();
      const code = String((block as any).code || (block as any).content || "").trim();
      if (code) {
        parts.push(`\`\`\`${language}\n${code}\n\`\`\``);
      }
    }
  }

  if (msg.sources?.length) {
    const sources = msg.sources
      .filter((source) => source.url)
      .map((source) => {
        const label = source.title || source.domain || source.url || "Source";
        return `${label}: ${source.url}`;
      });

    if (sources.length) {
      parts.push(`Sources:\n${sources.join("\n")}`);
    }
  }

  return parts.join("\n\n").trim();
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = value;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const copied = document.execCommand("copy");
      area.remove();
      return copied;
    } catch {
      return false;
    }
  }
}


function explicitCurrencyComponentRequest(query: string): boolean {
  return /\b(?:component|converter)\b/i.test(String(query || ""));
}


function explicitTodaysCurrencyComponentRequest(query: string): boolean {
  const q = String(query || "");
  return (
    /\b(?:today|today's|current|currently)\b/i.test(q) &&
    /\b(?:component|converter)\b/i.test(q)
  );
}

function sourceLabel(source: AISource): string {
  return String(
    source.title || source.domain || source.url || "Source",
  ).trim();
}

function sourceDomain(source: AISource): string {
  if (source.domain) return source.domain;
  try {
    return new URL(source.url || "").hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function sourceFavicon(source: AISource): string {
  try {
    const url = new URL(source.url || "");
    return `${url.origin}/favicon.ico`;
  } catch {
    return "";
  }
}

function sourceIconHtml(source: AISource, compact = false): string {
  const label = sourceLabel(source);
  const favicon = sourceFavicon(source);
  const klass = compact ? "aiz-source-avatar is-compact" : "aiz-source-avatar";

  if (!favicon) {
    return `<span class="${klass} aiz-source-avatar-letter">${escapeHtml(label.slice(0, 1).toUpperCase())}</span>`;
  }

  return `
    <span class="${klass}">
      <img
        src="${escapeHtml(favicon)}"
        alt=""
        loading="lazy"
        onerror="this.remove();this.parentElement?.classList.add('aiz-source-avatar-letter');this.parentElement.textContent='${escapeHtml(label.slice(0, 1).toUpperCase())}'"
      />
    </span>
  `;
}

function validSources(msg: Message): AISource[] {
  return (msg.sources || []).filter((source) => !!source.url);
}

function renderCompactSources(msg: Message): string {
  const sources = validSources(msg);
  if (!sources.length) return "";

  return `
    <div class="aiz-source-compact-row" aria-label="${sources.length} source${sources.length === 1 ? "" : "s"}">
      <div class="aiz-source-avatar-stack">
        ${sources
          .slice(0, 4)
          .map(
            (source) => `
              <a
                class="aiz-source-avatar-link"
                href="${escapeHtml(source.url || "")}"
                target="_blank"
                rel="noopener noreferrer"
                title="${escapeHtml(sourceLabel(source))}"
              >
                ${sourceIconHtml(source, true)}
              </a>
            `,
          )
          .join("")}
      </div>
      <span class="aiz-source-site-count">${sources.length} site${sources.length === 1 ? "" : "s"}</span>
    </div>
  `;
}

function renderFullSources(msg: Message): string {
  const sources = validSources(msg);
  if (!sources.length) return "";

  return `
    <div class="aiz-sources aiz-sources-active">
      <div class="aiz-sources-title">Sources</div>
      <div class="aiz-source-card-grid">
        ${sources
          .map((source) => {
            const label = sourceLabel(source);
            const domain = sourceDomain(source);
            return `
              <a
                class="aiz-source-card"
                href="${escapeHtml(source.url || "")}"
                target="_blank"
                rel="noopener noreferrer"
              >
                ${sourceIconHtml(source)}
                <span class="aiz-source-card-copy">
                  <span class="aiz-source-title">${escapeHtml(label)}</span>
                  ${
                    domain
                      ? `<span class="aiz-source-domain">${escapeHtml(domain)}</span>`
                      : ""
                  }
                </span>
                <span class="aiz-source-open" aria-hidden="true">↗</span>
              </a>
            `;
          })
          .join("")}
      </div>
    </div>
  `;
}


function weatherAnswerForRichText(text: string): string {
  const value = String(text || "").trim();
  if (!value) return "";

  // Preserve already-structured answers and never rewrite provider facts.
  if (/^\s*(?:#{2,4}\s|[-*•]\s|\d+[.)]\s)/m.test(value)) {
    return value;
  }

  const forecastMatch = value.match(/\bForecast:\s*(.+)$/i);
  let body = forecastMatch
    ? value.slice(0, forecastMatch.index).trim()
    : value;
  const forecastText = forecastMatch?.[1]?.trim() || "";

  const sentences =
    body.match(/[^.!?]+(?:[.!?]+|$)/g)?.map((item) => item.trim()).filter(Boolean) || [];

  if (!sentences.length) return value;

  const blocks: string[] = [sentences[0]];

  const rest = sentences.slice(1).join(" ");
  const conditionRows: string[] = [];

  const humidity = rest.match(/\bHumidity\s+(?:is|ranged from)\s+([^,.]+(?:\s+to\s+[^,.]+)?)/i);
  if (humidity?.[1]) {
    conditionRows.push(`- **Humidity:** ${humidity[1].trim()}`);
  }

  const wind = rest.match(/\bwind(?:s)?\s+(?:is|are|reached|reach)\s+(?:about\s+)?([^,.]+)/i);
  if (wind?.[1]) {
    conditionRows.push(`- **Wind:** ${wind[1].trim()}`);
  }

  const precipitation = rest.match(/\bprecipitation chance\s+is\s+([^,.]+)/i);
  if (precipitation?.[1]) {
    conditionRows.push(`- **Precipitation chance:** ${precipitation[1].trim()}`);
  }

  const temperatures = rest.match(/\btemperatures?\s+ranged from\s+([^,.]+)/i);
  if (temperatures?.[1]) {
    conditionRows.push(`- **Temperature range:** ${temperatures[1].trim()}`);
  }

  if (conditionRows.length) {
    blocks.push(`### Current conditions\n${conditionRows.join("\n")}`);
  } else if (sentences.length > 1) {
    const details = sentences
      .slice(1)
      .map((sentence) => `- ${sentence.replace(/[.!?]+$/, "")}`)
      .join("\n");
    if (details) {
      blocks.push(`### Details\n${details}`);
    }
  }

  if (forecastText) {
    const forecastRows = forecastText
      .split(";")
      .map((item) => item.trim().replace(/[.!?]+$/, ""))
      .filter(Boolean)
      .map((item) => {
        const match = item.match(/^([A-Za-z][A-Za-z ]*?)\s+(.+)$/);
        if (!match) return `- ${item}`;
        const label = match[1].trim();
        const values = match[2].trim().replace(/(\d)\/(\d)/g, "$1 / $2");
        return `- **${label}:** ${values}`;
      });

    if (forecastRows.length) {
      blocks.push(`### Forecast\n${forecastRows.join("\n")}`);
    }
  }

  return blocks.join("\n\n");
}

function renderAIMessageText(msg: Message): string {
  const responseType = String(msg.responseType || "").toLowerCase();
  const richText =
    responseType === "weather"
      ? weatherAnswerForRichText(msg.text)
      : msg.text;

  return renderAIRichText(richText, { variant: "chat" });
}

function lastAIMessageIndex(): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "ai") return index;
  }
  return -1;
}

function renderMessages() {
  const feed = document.getElementById("chat-feed");
  if (!feed) return;

  const activeAIIndex = lastAIMessageIndex();

  feed.innerHTML = messages
    .map(
      (msg, index) => `
      <div class="ai-msg ${msg.role}">
        ${
          msg.role === "ai" && index !== activeAIIndex && validSources(msg).length
            ? renderCompactSources(msg)
            : ""
        }
        <div class="msg-content">
          ${
            msg.role === "ai" && effectiveBatchItems(msg).length
              ? renderBatchItemsHtml(effectiveBatchItems(msg))
              : msg.role === "ai"
                ? renderAIMessageText(msg)
                : escapeHtml(msg.text).replace(/\n/g, "<br>")
          }
        </div>
        ${
          msg.role === "ai" && msg.showCurrencyComponent && msg.currency
            ? `<div class="aiz-inline-currency-component" id="aiz-currency-component-${index}"></div>`
            : ""
        }
        ${
          msg.role === "ai" && msg.codeBlocks?.length
            ? `<div class="msg-code-blocks" id="msg-code-${index}"></div>`
            : ""
        }
        ${
          msg.role === "ai"
            ? `
              ${
                msg.showAccuracyDisclaimer === false
                  ? ""
                  : `
                    <div class="aiz-response-disclaimer">
                      AI can make mistakes, so double-check responses
                    </div>
                  `
              }
              <div class="aiz-response-actions">
                <button
                  type="button"
                  class="aiz-response-action-btn"
                  data-aiz-copy="${index}"
                  aria-label="Copy full response"
                  title="Copy response"
                >
                  <span aria-hidden="true">⧉</span>
                </button>
                <button
                  type="button"
                  class="aiz-response-action-btn"
                  data-aiz-feedback="${index}"
                  aria-label="Send feedback about this response"
                  title="Feedback"
                  aria-expanded="false"
                >
                  <span aria-hidden="true">◇</span>
                </button>
              </div>
              <div
                class="aiz-feedback-host"
                id="aiz-feedback-host-${index}"
                hidden
              ></div>
              ${
                index === activeAIIndex && validSources(msg).length
                  ? renderFullSources(msg)
                  : ""
              }
            `
            : ""
        }
      </div>
    `,
    )
    .join("");

  messages.forEach((msg, index) => {
    if (msg.role !== "ai" || !msg.codeBlocks?.length) return;

    const codeContainer = document.getElementById(`msg-code-${index}`);
    if (codeContainer) {
      renderCodeBlocks(codeContainer, msg.codeBlocks);
    }
  });

  messages.forEach((msg, index) => {
    if (msg.role !== "ai") return;

    const fullText = fullMessageText(msg);
    const copyButton = feed.querySelector<HTMLButtonElement>(
      `[data-aiz-copy="${index}"]`,
    );
    const feedbackButton = feed.querySelector<HTMLButtonElement>(
      `[data-aiz-feedback="${index}"]`,
    );
    const feedbackHost = document.getElementById(`aiz-feedback-host-${index}`);

    copyButton?.addEventListener("click", async () => {
      const copied = await copyTextToClipboard(fullText);
      copyButton.classList.toggle("is-success", copied);
      copyButton.classList.toggle("is-error", !copied);
      copyButton.setAttribute(
        "aria-label",
        copied ? "Response copied" : "Copy failed",
      );

      window.setTimeout(() => {
        copyButton.classList.remove("is-success", "is-error");
        copyButton.setAttribute("aria-label", "Copy full response");
      }, 1200);
    });

    feedbackButton?.addEventListener("click", () => {
      if (!feedbackHost) return;

      const isOpen = !feedbackHost.hidden;
      feedbackHost.hidden = isOpen;
      feedbackButton.setAttribute("aria-expanded", String(!isOpen));

      if (!isOpen) {
        renderAIZoneFeedback(feedbackHost, fullText);
      } else {
        feedbackHost.innerHTML = "";
      }
    });
  });

  feed.scrollTop = feed.scrollHeight;
}

function getConversationId(): string {
  try {
    return window.localStorage.getItem(AI_ZONE_CONVERSATION_KEY) || "";
  } catch {
    return "";
  }
}

function saveConversationId(conversationId: string): void {
  if (!conversationId) return;

  try {
    window.localStorage.setItem(
      AI_ZONE_CONVERSATION_KEY,
      conversationId,
    );
  } catch {
    // Keep current request functional if storage is unavailable.
  }
}

async function handleSend(query: string) {
  if (!query.trim()) return;

  const fallbackShowAccuracyDisclaimer = !isHighConfidenceUtilityQuery(query);

  messages.push({ role: "user", text: query });
  renderMessages();

  const loadingId = `loading-${Date.now()}`;
  const feed = document.getElementById("chat-feed");
  if (!feed) return;

  const loadingHTML = `
    <div class="ai-msg ai" id="${loadingId}">
      <div class="typing-indicator">Thinking<span class="dots"></span></div>
    </div>`;

  feed.insertAdjacentHTML("beforeend", loadingHTML);
  feed.scrollTop = feed.scrollHeight;

  try {
    // Direct current-location questions are resolved by the same browser/IP
    // resolver used by weather. This prevents the language model from claiming
    // it cannot know location when AyeSearch has a valid locality context.
    if (isCurrentLocationQuery(query)) {
      const context = await getFreshWeatherLocationContext();
      document.getElementById(loadingId)?.remove();
      messages.push({
        role: "ai",
        showAccuracyDisclaimer: fallbackShowAccuracyDisclaimer,
        text: context?.displayLabel
          ? `Your current location is approximately ${context.displayLabel}.${context.location.source === "browser" ? " This is based on your device location." : " This is based on your network location; use device location for greater precision."}`
          : "I couldn't determine your current location. You can enable location access for a more precise result.",
      });
      renderMessages();
      return;
    }

    const conversationId = getConversationId();
    const {
      timezone,
      locale,
      timezoneOffsetMinutes,
    } = getClientTimeContext();

    const weatherLocationContext =
      isNearMeQuery(query)
        ? await getFreshWeatherLocationContext()
        : null;

    const res = await fetch(AI_ZONE_API_URL, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        q: query,
        conversation_id: conversationId || undefined,

        // Current device/browser time context.
        // IANA timezone is primary; UTC offset is fallback.
        timezone: timezone || undefined,
        timezone_offset_minutes: timezoneOffsetMinutes,
        locale: locale || undefined,
        // Send the timestamped structured browser location. The backend uses
        // updatedAt to enforce the same two-hour freshness rule for “near me”.
        // A plain coordinate string has no freshness proof and is intentionally
        // rejected for an explicit near-me request.
        weather_location: weatherLocationContext
          ? JSON.stringify(compactLocationHint(weatherLocationContext.location))
          : undefined,
        weather_display_location: weatherLocationContext?.displayLabel || undefined,
      }),
    });

    const contentType = res.headers.get("content-type") || "";
    const rawBody = await res.text();

    let data: AIZoneApiResponse = {};
    if (contentType.includes("application/json")) {
      try {
        data = JSON.parse(rawBody) as AIZoneApiResponse;
      } catch {
        data = {};
      }
    }

    if (!contentType.includes("application/json") && !res.ok) {
      throw new Error(`AI Zone returned HTTP ${res.status}`);
    }

    if (res.status === 429 && data.cooldown) {
      document.getElementById(loadingId)?.remove();

      messages.push({
        role: "ai",
        showAccuracyDisclaimer: fallbackShowAccuracyDisclaimer,
        text:
          data.answer ||
          "AI Zone is on cool down, we reached max searches, try again later",
        sources: [],
      });

      renderMessages();
      return;
    }

    if (!res.ok) {
      document.getElementById(loadingId)?.remove();

      messages.push({
        role: "ai",
        showAccuracyDisclaimer: fallbackShowAccuracyDisclaimer,
        text:
          data.answer ||
          (res.status === 503
            ? "AI Zone is temporarily unavailable. Please try again."
            : data.error || `AI Zone returned HTTP ${res.status}.`),
        sources: [],
      });

      renderMessages();
      return;
    }

    if (data.conversation_id) {
      saveConversationId(data.conversation_id);
    }

    const text =
      typeof data.answer === "string" && data.answer.trim()
        ? data.answer
        : data.code_blocks?.length
          ? ""
          : "Sorry, I couldn't generate a response.";

    document.getElementById(loadingId)?.remove();

    messages.push({
      role: "ai",
      showAccuracyDisclaimer: shouldShowAccuracyDisclaimer(data, query),
      responseType: String(data.response_type || ""),
      text,
      batchItems:
        data.batch === true && Array.isArray(data.items)
          ? data.items
          : [],
      codeBlocks: Array.isArray(data.code_blocks)
        ? data.code_blocks
        : [],
      // Backend guarantees these are only Groq-returned search result URLs.
      sources: Array.isArray(data.sources) ? data.sources : [],
      currency: data.currency,
      showCurrencyComponent:
        String(data.response_type || "").toLowerCase() === "currency" &&
        !!data.currency &&
        explicitCurrencyComponentRequest(query),
      forceTodaysCurrencyComponent:
        String(data.response_type || "").toLowerCase() === "currency" &&
        !!data.currency &&
        explicitTodaysCurrencyComponentRequest(query),
    });

    renderMessages();
  } catch (err) {
    console.error("AI Zone request failed:", err);

    document.getElementById(loadingId)?.remove();

    messages.push({
      role: "ai",
      showAccuracyDisclaimer: fallbackShowAccuracyDisclaimer,
      text: "Error connecting to AI. Please try again later.",
    });

    renderMessages();
  }
}
